import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { imageSize, capitalizeFirstLetter } from '@common/react/utils/utils';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import LinkWithLang from '@common/react/components/UI/lang/LinkWithLang/LinkWithLang';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';

import { Doctor } from '@commonTuna/react/objects/BaseDoctor';
import { DoctorProfession } from '@commonTuna/react/objects/DoctorProfession';
import { DoctorLocation } from '@commonTuna/react/objects/DoctorLocation';

import ImageWithSkeleton from '@app/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import LocationName from '@app/components/UI/LocationName/LocationName';
import { useReduxContext } from '@app/hooks/useReduxContext';

import '@app/scss/pages/doctorList.scss';

const Doctors: React.FC = () => {
	const { t, i18n } = useTranslation();

	const langString = capitalizeFirstLetter(i18n.language);

	const title = t('seo.doctorsPage.title');

	const { offices } = useReduxContext();

	return <ServerPageProvider
		inner={(serverPage) =>
			<div className="doctors-page inner-page container">
				<BreadCrumbs
					title={title}
					links={[{ title: t('seo.practicePage.title'), path: t('seo.practicePage.link') }]}
				/>
				<div className="inner-page__headline text-center">
					<h1>{title}</h1>
				</div>
				{serverPage.items && serverPage.items.list.map((doctor: Doctor) => {
					if (!doctor[`name${langString}`] || doctor[`name${langString}`] === ' ') {
						return null;
					}

					const path = doctor[`path${langString}`];

					return <div className="doctor-card clearfix" key={doctor.id}>
						{path
							? <LinkWithLang to={`doctor/${path}`} className="doctor-card__image">
								<img src={doctor.originalAvatar || doctor.avatar} alt="Doctor avatar" />
								{doctor.certificates?.length > 0
								&& <div className="doctor-certificates">
									{doctor.certificates
										.filter((item) => item.certificate.image)
										.map((item, i) =>
											<div key={item.id} className="certificate-wrapper">
												<div>
													<ImageWithSkeleton
														alt={item.certificate.name}
														key={item.id}
														src={imageSize(item.certificate.image, '_thumb')}
													/>
												</div>
											</div>)}
								</div>
								}
							</LinkWithLang>
							: <div className="doctor-card__image">
								<img src={doctor.originalAvatar || doctor.avatar} alt="Doctor avatar" />
								{doctor.certificates?.length > 0
								&& <div className="doctor-certificates">
									{doctor.certificates
										.filter((item) => item.certificate.image)
										.map((item, i) =>
											<div key={item.id} className="certificate-wrapper">
												<div>
													<ImageWithSkeleton
														alt={item.certificate.name}
														key={item.id}
														src={imageSize(item.certificate.image, '_thumb')}
													/>
												</div>
											</div>)}
								</div>
								}
							</div>
						}
						<div className="doctor-card__body">
							{path
								? <LinkWithLang to={`doctor/${path}`} className="doctor-card__name inner-page__headline">
									<h2>
										{doctor[`name${langString}`]}
									</h2>
								</LinkWithLang>
								: <h2>
									{doctor[`name${langString}`]}
								</h2>
							}
							<div className="doctor-card__professions mobile-hidden">
								{doctor.professions && doctor.professions.map((profession: DoctorProfession, index: number) => (
									profession.profession[`name${langString}`] + (index !== doctor.professions.length - 1 ? ', ' : '')
								))}
							</div>
							<div className="doctor-card__location inline-tags mobile-hidden">
								{doctor.locations && doctor.locations.map((location: DoctorLocation, index: number) =>
									<span className="inline-tag" key={location.id}>
										<LocationName item={offices.find((office) => office.id === location.location.id) || location.location} />
									</span>)}
							</div>
							<div className="doctor-card__tags mobile-show inline-tags clearfix">
								{doctor.professions && doctor.professions.map((profession: DoctorProfession, index: number) =>
									<div className="doctor-card__professions" key={profession.id}>
										{profession.profession[`name${langString}`] + (index !== doctor.professions.length - 1 ? ', ' : '')}
									</div>)}
								{doctor.locations && doctor.locations.map((location: DoctorLocation, index: number) =>
									<span className="inline-tag" key={location.id}>
										<LocationName item={offices.find((office) => office.id === location.location.id) || location.location} />
									</span>)}
							</div>
							{doctor[`description${langString}`]
							&& <div
								className="doctor-card__description external-html"
								dangerouslySetInnerHTML={{ __html: doctor[`description${langString}`] }}
							/>
							}
						</div>
					</div>;
				})}
			</div>}
	/>;
};

export default Doctors;
